import {
  Button,
  Card,
  CardContent,
  Stack,
  Typography,
  useTheme,
  TextField,
  useMediaQuery,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectUserInfo } from "../../user/UserSlice";
import {
  selectPaymentStatus,
  selectPayments,
  selectPaymentErrors,
  selectPaymentSuccessMessage,
  fetchPaymentsByUserIdAsync,
  createPaymentAsync,
  updatePaymentByIdAsync,
  resetPaymentStatus,
} from "../PaymentSlice";
import { useForm } from "react-hook-form";
import { LoadingButton } from "@mui/lab";
import { toast } from "react-toastify";

export const PaymentPage = () => {
  const dispatch = useDispatch();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();
  const status = useSelector(selectPaymentStatus);
  const userInfo = useSelector(selectUserInfo);
  const payments = useSelector(selectPayments);
  const error = useSelector(selectPaymentErrors);
  const successMessage = useSelector(selectPaymentSuccessMessage);
  const theme = useTheme();
  const [addPayment, setAddPayment] = useState(false);
  const [editPaymentId, setEditPaymentId] = useState(null);

  const is900 = useMediaQuery(theme.breakpoints.down(900));
  const is480 = useMediaQuery(theme.breakpoints.down(480));

  useEffect(() => {
    if (userInfo?._id) {
      dispatch(fetchPaymentsByUserIdAsync(userInfo._id));
    }
  }, [userInfo?._id, dispatch]);

  useEffect(() => {
    if (successMessage) {
      toast.success(successMessage);
      dispatch(resetPaymentStatus());
    }
    if (error) {
      toast.error(error.message || "Payment operation failed");
      dispatch(resetPaymentStatus());
    }
  }, [successMessage, error, dispatch]);

  const handleAddPayment = (data) => {
    const paymentData = { ...data, user: userInfo._id };
    dispatch(createPaymentAsync(paymentData));
    setAddPayment(false);
    reset();
  };

  const handleUpdatePayment = (data) => {
    const updateData = { ...data, _id: editPaymentId };
    dispatch(updatePaymentByIdAsync(updateData));
    setEditPaymentId(null);
    reset();
  };

  return (
    <Stack
      height={"calc(100vh - 4rem)"}
      justifyContent={"flex-start"}
      alignItems={"center"}
    >
      <Stack
        width={is900 ? "100%" : "50rem"}
        p={2}
        mt={is480 ? 0 : 5}
        rowGap={2}
      >
        <Typography variant="h5" gutterBottom>
          Payment Methods
        </Typography>

        {!addPayment && !editPaymentId && (
          <Button
            onClick={() => setAddPayment(true)}
            variant="contained"
            size={is480 ? "small" : "medium"}
          >
            Add Payment Method
          </Button>
        )}

        {(addPayment || editPaymentId) && (
          <Card>
            <CardContent>
              <Stack
                component="form"
                onSubmit={handleSubmit(
                  editPaymentId ? handleUpdatePayment : handleAddPayment
                )}
                rowGap={2}
              >
                <Typography variant="h6">
                  {editPaymentId
                    ? "Edit Payment Method"
                    : "Add New Payment Method"}
                </Typography>

                <TextField
                  label="Card Number"
                  {...register("cardNumber", { required: true })}
                  fullWidth
                  error={!!errors.cardNumber}
                  helperText={errors.cardNumber && "Card number is required"}
                />

                <TextField
                  label="Card Holder Name"
                  {...register("cardHolder", { required: true })}
                  fullWidth
                  error={!!errors.cardHolder}
                  helperText={
                    errors.cardHolder && "Card holder name is required"
                  }
                />

                <Stack direction="row" spacing={2}>
                  <TextField
                    label="Expiry Date"
                    {...register("expiry", { required: true })}
                    fullWidth
                    error={!!errors.expiry}
                    helperText={errors.expiry && "Expiry date is required"}
                  />
                  <TextField
                    label="CVV"
                    type="password"
                    {...register("cvv", { required: true })}
                    fullWidth
                    error={!!errors.cvv}
                    helperText={errors.cvv && "CVV is required"}
                  />
                </Stack>

                <Stack direction="row" spacing={2} justifyContent="flex-end">
                  <LoadingButton
                    type="submit"
                    variant="contained"
                    loading={status === "pending"}
                  >
                    {editPaymentId ? "Update" : "Save"}
                  </LoadingButton>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setAddPayment(false);
                      setEditPaymentId(null);
                      reset();
                    }}
                  >
                    Cancel
                  </Button>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        )}

        <Stack spacing={2}>
          {payments.length > 0 ? (
            payments.map((payment) => (
              <Card key={payment._id}>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between">
                    <div>
                      <Typography variant="h6">
                        •••• •••• •••• {payment.cardNumber.slice(-4)}
                      </Typography>
                      <Typography>Exp: {payment.expiry}</Typography>
                      <Typography>{payment.cardHolder}</Typography>
                    </div>
                    <Stack direction="row" spacing={1}>
                      <Button
                        size="small"
                        onClick={() => {
                          setEditPaymentId(payment._id);
                          reset({
                            cardNumber: payment.cardNumber,
                            cardHolder: payment.cardHolder,
                            expiry: payment.expiry,
                            cvv: "",
                          });
                        }}
                      >
                        Edit
                      </Button>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            ))
          ) : (
            <Typography textAlign="center" mt={2}>
              No payment methods saved
            </Typography>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};
